import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import VirtualizedDropdown, { VirtualizedDropdownOption } from '../common/VirtualizedDropdown';
import { API_ENDPOINTS, STORAGE_KEYS } from '../../config/api.config';
import { Button } from '@ohif/ui-next';
import { isStudyAssigned, type SelectedStudy } from '../../utils/studyUtils';
import { getAuthHeaders } from '../../utils/studyAssignmentUtils';
import { assignPeerReview } from '../../services/peerReviewService';
import {
  formatPriorityDisplay,
  priorityUsesUrgentPill,
} from '../worklist/imagingWorklistTableUtils';

interface AssignPeerReviewerModalProps {
  checkedStudyIds: string[];
  selectedStudies?: SelectedStudy[];
  onClose: () => void;
  onSuccess?: () => void;
  servicesManager?: {
    services?: {
      uiNotificationService?: {
        show: (options: {
          title: string;
          message: string;
          type: 'success' | 'error' | 'warning' | 'info';
          duration?: number;
          allowDuplicates?: boolean;
        }) => void;
      };
    };
  };
}

interface PeerReviewerCandidate {
  id: number;
  full_name: string;
  reporting_modality_access?: string[];
  peer_reviewer?: boolean;
  designation?: string;
  sub_role?: string;
}

function isAsciiWhitespace(ch: string): boolean {
  return ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\f' || ch === '\v';
}

/** Remove "(Senior)" / "(Junior)" at end only (optional surrounding ASCII whitespace). */
function stripSubRoleSuffixFromAssignedName(name: string): string {
  let result = name;
  for (const role of ['Senior', 'Junior'] as const) {
    const token = `(${role})`;
    let end = result.length;
    while (end > 0 && isAsciiWhitespace(result.charAt(end - 1))) {
      end--;
    }
    const withoutTrailingWs = result.slice(0, end);
    if (!withoutTrailingWs.toLowerCase().endsWith(token.toLowerCase())) {
      continue;
    }
    let start = withoutTrailingWs.length - token.length;
    while (start > 0 && isAsciiWhitespace(withoutTrailingWs.charAt(start - 1))) {
      start--;
    }
    result = withoutTrailingWs.slice(0, start);
  }
  return result.trim();
}

function formatReviewerSubtitle(radiologist: PeerReviewerCandidate, emptyFallback: string): string {
  if (radiologist.designation?.trim()) {
    return radiologist.designation.trim();
  }
  const list = (radiologist.reporting_modality_access || [])
    .map(entry => String(entry).trim())
    .filter(Boolean);
  if (list.length === 0) {
    return emptyFallback;
  }
  return list.join(', ');
}

function getPeerReviewAssignValidationError(params: {
  selectedReviewer: number | null;
  studiesForDisplay: SelectedStudy[];
  t: (key: string) => string;
}): string | null {
  const { selectedReviewer, studiesForDisplay, t } = params;
  if (selectedReviewer === null) {
    return t('Please select a peer reviewer');
  }
  if (studiesForDisplay.length === 0) {
    return t('No studies selected');
  }
  const nonApproved = studiesForDisplay.filter(
    study => study.reportStatus?.trim().toLowerCase() !== 'approved'
  );
  if (nonApproved.length > 0) {
    return t('Peer review can only be assigned to studies with an Approved report status.');
  }
  return null;
}

async function assignPeerReviewsToStudies(params: {
  studies: SelectedStudy[];
  peerReviewerId: number;
}): Promise<{ results: unknown[]; errors: string[] }> {
  const results = [];
  const errors: string[] = [];

  for (const study of params.studies) {
    try {
      const payload: {
        peer_reviewer_id: number;
        study_instance_uid?: string;
        study_id?: number;
      } = {
        peer_reviewer_id: params.peerReviewerId,
        study_instance_uid: study.studyInstanceUid,
      };
      if (study.studyId != null && study.studyId !== '') {
        const numericId = Number(study.studyId);
        if (!Number.isNaN(numericId) && numericId > 0) {
          payload.study_id = numericId;
        }
      }
      const created = await assignPeerReview(payload);
      results.push(created);
    } catch (err) {
      const patientLabel = study.patientName?.trim() || study.studyInstanceUid;
      const message = err instanceof Error ? err.message : 'Assignment failed';
      errors.push(`${patientLabel}: ${message}`);
    }
  }

  return { results, errors };
}

const AssignPeerReviewerModal: React.FC<AssignPeerReviewerModalProps> = ({
  checkedStudyIds,
  selectedStudies = [],
  onClose,
  onSuccess,
  servicesManager,
}) => {
  const { t } = useTranslation();
  const [reviewers, setReviewers] = useState<VirtualizedDropdownOption[]>([]);
  const [selectedReviewer, setSelectedReviewer] = useState<number | null>(null);
  const [selectedReviewerName, setSelectedReviewerName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const uiNotificationService = servicesManager?.services?.uiNotificationService;

  const studiesForDisplay = useMemo(() => {
    if (!checkedStudyIds.length) {
      return selectedStudies;
    }
    return checkedStudyIds
      .map(uid => selectedStudies.find(s => s.studyInstanceUid === uid))
      .filter((s): s is SelectedStudy => Boolean(s));
  }, [checkedStudyIds, selectedStudies]);

  /** Exclude the original report / study radiologist (by assigned name on the worklist). */
  const excludedRadiologistNames = useMemo(() => {
    const names = new Set<string>();
    selectedStudies.forEach(study => {
      if (isStudyAssigned(study) && study.assignedName) {
        names.add(stripSubRoleSuffixFromAssignedName(study.assignedName).toLowerCase());
      }
    });
    return Array.from(names);
  }, [selectedStudies]);

  const fetchPeerReviewers = useCallback(
    async (search: string = '') => {
      setIsLoading(true);
      try {
        const facilityId = localStorage.getItem(STORAGE_KEYS.FACILITY_ID);
        const headers = getAuthHeaders();

        // Same Active Radiologist + facility query as Assign Radiologist.
        // Exclude only the original reporter; peer_reviewer flag is not required.
        const params: {
          page?: number;
          limit?: number;
          role?: string;
          status?: string;
          facility_id?: number;
          search?: string;
          name?: string;
        } = {
          page: 1,
          limit: 100,
          role: 'Radiologist',
          status: 'Active',
        };

        if (facilityId) {
          const facilityIdNumber = parseInt(facilityId, 10);
          if (!Number.isNaN(facilityIdNumber)) {
            params.facility_id = facilityIdNumber;
          }
        }

        if (search) {
          params.search = search;
          params.name = search;
        }

        const url = API_ENDPOINTS.RADIOLOGISTS.GET(params);
        const response = await fetch(url, {
          method: 'GET',
          headers,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch peer reviewers: ${response.statusText}`);
        }

        const responseData = await response.json();

        const formattedReviewers: VirtualizedDropdownOption[] =
          responseData.success && Array.isArray(responseData.data)
            ? responseData.data
                .map((radiologist: PeerReviewerCandidate) => ({
                  id: radiologist.id,
                  label: radiologist.full_name || '',
                  subtitle: formatReviewerSubtitle(radiologist, t('Radiologist')),
                }))
                .filter(radiologist => {
                  if (!radiologist.label.trim()) {
                    return false;
                  }
                  const nameLower = stripSubRoleSuffixFromAssignedName(
                    radiologist.label
                  ).toLowerCase();
                  return !excludedRadiologistNames.includes(nameLower);
                })
            : [];

        setReviewers(formattedReviewers);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch peer reviewers';
        if (uiNotificationService) {
          uiNotificationService.show({
            title: t('Error'),
            message: errorMessage,
            type: 'error',
            duration: 5000,
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [excludedRadiologistNames, t, uiNotificationService]
  );

  useEffect(() => {
    fetchPeerReviewers('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchPeerReviewers(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleReviewerChange = (value: number | null, label: string) => {
    setSelectedReviewer(value);
    setSelectedReviewerName(label);
  };

  const handleAssign = async () => {
    const validationError = getPeerReviewAssignValidationError({
      selectedReviewer,
      studiesForDisplay,
      t,
    });
    if (validationError) {
      uiNotificationService?.show({
        title: t('Validation Error'),
        message: validationError,
        type: 'error',
        duration: validationError.includes('Approved') ? 5000 : 3000,
        allowDuplicates: true,
      });
      return;
    }

    if (selectedReviewer == null) {
      return;
    }

    setIsAssigning(true);

    try {
      const { results, errors } = await assignPeerReviewsToStudies({
        studies: studiesForDisplay,
        peerReviewerId: selectedReviewer,
      });

      if (results.length > 0 && uiNotificationService) {
        uiNotificationService.show({
          title: t('Success'),
          message: t('Successfully assigned {{count}} study(ies) for peer review to {{reviewer}}', {
            count: results.length,
            reviewer: selectedReviewerName,
          }),
          type: 'success',
          duration: 5000,
        });
      }

      if (errors.length > 0 && uiNotificationService) {
        uiNotificationService.show({
          title: t('Assignment Failed'),
          message: errors.join('; '),
          type: 'error',
          duration: 8000,
          allowDuplicates: true,
        });
      }

      if (results.length > 0) {
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('Failed to assign peer review');
      uiNotificationService?.show({
        title: t('Assignment Failed'),
        message: errorMessage,
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const studyCount = studiesForDisplay.length || checkedStudyIds.length;

  const priorityBadgeLabel = (study: SelectedStudy) => {
    if (study.isCritical) {
      return t('Critical');
    }
    const formatted = formatPriorityDisplay(study.priority);
    if (formatted) {
      return formatted;
    }
    return t('Routine');
  };

  const priorityBadgeUrgent = (study: SelectedStudy) =>
    Boolean(study.isCritical || priorityUsesUrgentPill(study.priority));

  return (
    <div className="-mt-2 font-sans text-[#000000]">
      <div className="border-b border-[#E0E0E0] px-6 pb-4 pr-14 pt-6">
        <h2 className="text-[22px] font-semibold leading-snug tracking-tight text-[#000000]">
          {t('Assign Peer Review')}
        </h2>
        <p className="mt-1.5 text-sm leading-snug text-[#717171]">
          {t(
            'Select a peer reviewer to assign the selected approved studies for blinded peer review'
          )}
        </p>
      </div>

      <div className="flex flex-col gap-6 px-6 py-6">
        <section>
          <h3 className="mb-3 text-sm font-semibold text-[#000000]">
            {t('Selected Studies')} ({studyCount})
          </h3>
          <div className="flex max-h-52 flex-col gap-2 overflow-y-auto pr-0.5">
            {studiesForDisplay.map(study => {
              const pid = study.patientId?.trim();
              const metaLine = pid ? `UHID-${pid}` : '';
              const urgent = priorityBadgeUrgent(study);
              const showCurrentAssignee = isStudyAssigned(study) && study.assignedName?.trim();
              return (
                <div
                  key={study.studyInstanceUid}
                  className="rounded-lg border border-[#E0E0E0] bg-white px-4 py-3"
                >
                  <div className="text-sm font-medium text-[#333333]">
                    {study.patientName?.trim() || t('Unknown patient')}
                  </div>
                  <div className="mt-0.5 text-sm font-normal text-[#666666]">
                    {study.studyDescription?.trim() ||
                      study.modalities?.trim() ||
                      t('Study description unavailable')}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#666666]">
                    {metaLine ? <span className="min-w-0 break-words">{metaLine}</span> : null}
                    <span
                      className={
                        urgent
                          ? 'inline-flex shrink-0 rounded-full bg-[#FEE2E2] px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-[#B91C1C]'
                          : 'inline-flex shrink-0 rounded-full bg-[#F0F0F0] px-2.5 py-0.5 text-[11px] font-medium text-[#333333]'
                      }
                    >
                      {priorityBadgeLabel(study)}
                    </span>
                  </div>
                  {showCurrentAssignee ? (
                    <div className="mt-2 text-xs leading-snug text-[#666666]">
                      {t('Original radiologist')}:{' '}
                      {stripSubRoleSuffixFromAssignedName(study.assignedName!.trim())}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-[#000000]">
            {t('Select Peer Reviewer')}
          </label>
          <VirtualizedDropdown
            value={selectedReviewer}
            onChange={handleReviewerChange}
            options={reviewers}
            placeholder={t('Choose a peer reviewer...')}
            isLoading={isLoading}
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder={t('Search peer reviewers...')}
            emptyMessage={t('No eligible peer reviewers found')}
            loadingMessage={t('Loading peer reviewers...')}
            className="w-full"
            showNone={false}
            variant="light"
            truncateTriggerLabel={false}
            richSubtitlePrimaryClassName="w-full text-sm font-medium leading-tight text-[#333333]"
            richSubtitleSecondaryClassName="w-full text-xs font-normal leading-snug text-[#9ca3af]"
          />
          <p className="text-xs leading-relaxed text-[#717171]">
            {t(
              'Active radiologists at this facility are listed. The original reporting radiologist is excluded.'
            )}
          </p>
        </section>
      </div>

      <div className="flex flex-wrap justify-end gap-3 border-t border-[#E0E0E0] px-6 py-4">
        <Button
          variant="outline"
          onClick={onClose}
          className="min-w-[96px] rounded-md border-[#E0E0E0] bg-white px-4 py-2 text-[#000000] hover:bg-[#FAFAFA]"
          disabled={isAssigning}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="default"
          onClick={handleAssign}
          disabled={selectedReviewer === null || isLoading || isAssigning}
          className="min-w-[120px] rounded-md border-0 px-4 py-2 text-white hover:brightness-95 disabled:opacity-50 disabled:hover:brightness-100"
          style={{ backgroundColor: '#118ab2' }}
        >
          {isAssigning ? t('Assigning...') : t('Assign Peer Review')}
        </Button>
      </div>
    </div>
  );
};

export default AssignPeerReviewerModal;
